import {ClosedProjectIcon} from '@github-ui/custom-icons/ClosedProjectIcon'
import {TEST_IDS} from '@github-ui/issue-body/constants/test-ids'
import {DEFAULT_RELAY_CACHE_TTL} from '@github-ui/relay-query-cache/helpers'
import {ChevronDownIcon, ChevronUpIcon, ProjectTemplateIcon, TableIcon} from '@primer/octicons-react'
import {IconButton, Link, Truncate} from '@primer/react'
import {useCallback, useMemo, useSyncExternalStore} from 'react'
import {fetchQuery, graphql, useFragment, useRelayEnvironment} from 'react-relay'

import {LABELS} from '../../constants/labels'
import type {ProjectItemSectionFieldListQuery} from './__generated__/ProjectItemSectionFieldListQuery.graphql'
import type {ProjectItemSectionView$key} from './__generated__/ProjectItemSectionView.graphql'
import {SingleSelectField} from './fields/SingleSelectField'
import {ProjectItemSectionFieldListGraphqlQuery} from './ProjectItemSectionFieldList'
import styles from './ProjectItemSectionView.module.css'

const AUTO_ASSIGN_AGENT_TRIGGER_TYPE = 'auto_assign_agent'
const GET_PROJECT_ITEMS_ACTION_TYPE = 'get_project_items'
const MEMEX_WORKFLOWS_DATA_ID = 'memex-workflows-data'
const JSON_ISLAND_ROOT_ID = 'json-island-root'

const EMPTY_AUTO_ASSIGN_WORKFLOW_OPTION_IDS: ReadonlySet<string> = new Set<string>()

// useSyncExternalStore helpers: the JSON island contents do not change after
// page load, so subscribing is a no-op and the "server" snapshot is empty.
function subscribeAutoAssignWorkflows(): () => void {
  return () => {}
}

function getEmptyAutoAssignWorkflowOptionIds(): ReadonlySet<string> {
  return EMPTY_AUTO_ASSIGN_WORKFLOW_OPTION_IDS
}

type MemexWorkflowAction = {
  actionType?: string
  arguments?: {
    fieldId?: number
    fieldOptionId?: string
  }
}

type MemexWorkflow = {
  triggerType?: string
  enabled?: boolean
  actions?: MemexWorkflowAction[]
}

function fetchMemexWorkflowsData(): MemexWorkflow[] {
  if (typeof document === 'undefined') return []

  const root = document.getElementById(JSON_ISLAND_ROOT_ID)
  const jsonIsland = root
    ? root.querySelector(`:scope > script#${MEMEX_WORKFLOWS_DATA_ID}`)
    : document.getElementById(MEMEX_WORKFLOWS_DATA_ID)

  if (!(jsonIsland instanceof HTMLScriptElement) || !jsonIsland.textContent) return []

  try {
    const workflows: unknown = JSON.parse(jsonIsland.textContent)
    return Array.isArray(workflows) ? (workflows as MemexWorkflow[]) : []
  } catch {
    return []
  }
}

function getAutoAssignWorkflowOptionIds(statusFieldDatabaseId: number | null | undefined): ReadonlySet<string> {
  if (statusFieldDatabaseId == null) return EMPTY_AUTO_ASSIGN_WORKFLOW_OPTION_IDS

  return fetchMemexWorkflowsData().reduce((optionIds, workflow) => {
    if (workflow.triggerType !== AUTO_ASSIGN_AGENT_TRIGGER_TYPE || !workflow.enabled) return optionIds

    const sourceAction = workflow.actions?.find(action => action.actionType === GET_PROJECT_ITEMS_ACTION_TYPE)
    if (!sourceAction?.arguments?.fieldOptionId || sourceAction.arguments.fieldId !== statusFieldDatabaseId) {
      return optionIds
    }

    optionIds.add(sourceAction.arguments.fieldOptionId)
    return optionIds
  }, new Set<string>())
}

export type ProjectItemSectionViewProps = {
  projectItem: ProjectItemSectionView$key
  open: boolean
  setOpen: (isOpen: boolean) => void
  onIssueUpdate: () => void
  isSelectedProject: boolean
  isProjectClosed: boolean
}

export function ProjectItemSectionView({
  projectItem,
  setOpen,
  open,
  onIssueUpdate,
  isSelectedProject,
  isProjectClosed,
}: ProjectItemSectionViewProps) {
  const environment = useRelayEnvironment()
  const {id, project, fieldValueByName, isArchived} = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment ProjectItemSectionView on ProjectV2Item {
        id
        isArchived
        project {
          id
          title
          template
          viewerCanUpdate
          url
          field(name: "Status") {
            ... on ProjectV2SingleSelectField {
              databaseId
            }
            ...SingleSelectFieldConfigFragment @dangerously_unaliased_fixme
          }
        }
        fieldValueByName(name: "Status") {
          ...SingleSelectFieldFragment @dangerously_unaliased_fixme
        }
      }
    `,
    projectItem,
  )

  const prefetchFieldList = useCallback(() => {
    if (!open) {
      fetchQuery<ProjectItemSectionFieldListQuery>(
        environment,
        ProjectItemSectionFieldListGraphqlQuery,
        {
          id,
        },
        {fetchPolicy: 'store-or-network', networkCacheConfig: {metadata: {ttl: DEFAULT_RELAY_CACHE_TTL}}},
      ).subscribe({})
    }
  }, [environment, id, open])

  const projectIcon = isProjectClosed ? (
    <ClosedProjectIcon className={styles.projectIcon} />
  ) : project.template ? (
    <ProjectTemplateIcon className={styles.projectIcon} aria-hidden="true" />
  ) : (
    <TableIcon className={styles.projectIcon} aria-hidden="true" />
  )
  const statusFieldDatabaseId = project.field?.databaseId
  // Read the JSON island via useSyncExternalStore so the server renders an
  // empty set (matching the SSR markup) while the client renders the parsed
  // workflow set without a hydration mismatch warning.
  const clientSnapshot = useMemo(() => getAutoAssignWorkflowOptionIds(statusFieldDatabaseId), [statusFieldDatabaseId])
  const autoAssignWorkflowOptionIds = useSyncExternalStore(
    subscribeAutoAssignWorkflows,
    () => clientSnapshot,
    getEmptyAutoAssignWorkflowOptionIds,
  )

  return (
    <div className={styles.ProjectItemSectionViewContainer}>
      {isSelectedProject ? (
        <span className={styles.projectTitleText}>
          {projectIcon}
          <span className={styles.truncatedProjectTitle}>{project.title}</span>
          {isProjectClosed && <span className="sr-only">Closed</span>}
        </span>
      ) : (
        <div className={styles.projectLinkContainer}>
          <Link href={project.url} hoverColor={'var(--fgColor-accent)'} tabIndex={0} className={styles.projectLink}>
            {projectIcon}
            <Truncate title={project.title} data-testid={TEST_IDS.projectTitle} className={styles.Truncate}>
              {project.title}
            </Truncate>
            {isProjectClosed && <span className={styles.closedLabel}>Closed</span>}
          </Link>
        </div>
      )}
      <div className={styles.statusFieldContainer}>
        {project.field && fieldValueByName !== undefined && (
          <SingleSelectField
            viewerCanUpdate={project.viewerCanUpdate && !isArchived}
            itemId={id}
            projectId={project.id}
            field={project.field}
            value={fieldValueByName}
            onIssueUpdate={onIssueUpdate}
            isStatusField
            autoAssignWorkflowOptionIds={autoAssignWorkflowOptionIds}
          />
        )}
        <IconButton
          variant="invisible"
          onClick={() => {
            setOpen(!open)
          }}
          onPointerEnter={prefetchFieldList}
          size="small"
          icon={!open ? ChevronDownIcon : ChevronUpIcon}
          aria-label={!open ? LABELS.showMoreProjectFields : LABELS.hideMoreProjectFields}
          aria-expanded={open}
        />
      </div>
    </div>
  )
}
